import React, { useRef, useState } from "react";
import { Alert, Button, Col, Container, Form, Row, Spinner } from "react-bootstrap";
import { FaLocationArrow } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import AppTopbar from "../components/AppTopbar";
import LocationSelector from "../components/LocationSelector";
import axiosInstance from "../config/axiosConfig";
import { ROUTES } from "../config/appConfig";
import { companyInfo } from "../config/companyConfig";
import { extractPayload, getErrorMessage } from "../utils/apiResponseUtils";
import "../styles/Landing.css";

const quickHighlights = [
  "Same-day delivery",
  "Transparent pricing",
  "4 vehicle options",
  "Booking in minutes",
];

const faqItems = [
  {
    question: "Do I need to log in before checking my route?",
    answer:
      "No. You can set pickup and drop on the landing page first, then continue through the next two public steps before login.",
  },
  {
    question: "When do I choose the vehicle?",
    answer:
      "Vehicle selection happens on the second page after you finish the route step.",
  },
  {
    question: "When is the fare shown?",
    answer:
      "The fare estimate is shown on the third page after the route and vehicle are ready.",
  },
];

const Landing = () => {
  const navigate = useNavigate();
  const [pincode, setPincode] = useState("");
  const [serviceability, setServiceability] = useState(null);
  const [serviceabilityLoading, setServiceabilityLoading] = useState(false);
  const [serviceabilityError, setServiceabilityError] = useState("");
  const [pickupLocation, setPickupLocation] = useState(null);
  const [dropLocation, setDropLocation] = useState(null);
  const [openFaqIndex, setOpenFaqIndex] = useState(0);
  const [routeServiceabilityErrors, setRouteServiceabilityErrors] = useState([]);
  const [routeServiceabilityLoading, setRouteServiceabilityLoading] = useState(false);
  const routeServiceabilityErrorRef = useRef(null);

  const serviceable = serviceability?.serviceable === true;
  const routeReady = Boolean(pickupLocation && dropLocation);

  const handleCheckServiceability = async () => {
    if (pincode.length !== 6) {
      setServiceabilityError("Enter a valid 6-digit pickup pincode.");
      return;
    }

    setServiceabilityLoading(true);
    setServiceabilityError("");
    setRouteServiceabilityErrors([]);
    setServiceability(null);
    setPickupLocation(null);
    setDropLocation(null);

    try {
      const response = await axiosInstance.post("/content/serviceability", { pincode });
      setServiceability(extractPayload(response.data) || {});
    } catch (error) {
      setServiceabilityError(getErrorMessage(error, "Unable to check serviceability right now."));
    } finally {
      setServiceabilityLoading(false);
    }
  };

  const extractLocationPincode = (location, fallbackPincode = "") => {
    const text = [
      location?.pincode,
      location?.postcode,
      location?.postalCode,
      location?.postal_code,
      location?.name,
      location?.address,
      fallbackPincode,
    ].filter(Boolean).join(" ");
    const match = text.match(/\b\d{6}\b/);

    return match?.[0] || "";
  };

  const checkLocationServiceability = async (location, label, fallbackPincode = "") => {
    const locationPincode = extractLocationPincode(location, fallbackPincode);

    if (!locationPincode) {
      return {
        serviceable: false,
        message: `${label} pincode could not be detected. Please choose a more precise location.`,
      };
    }

    try {
      const response = await axiosInstance.post("/content/serviceability", { pincode: locationPincode });
      const payload = extractPayload(response.data) || {};
      const isServiceable = payload?.serviceable === true;

      return {
        serviceable: isServiceable,
        message: isServiceable ? "" : `${label} location is not serviceable for pincode ${locationPincode}.`,
      };
    } catch (error) {
      return {
        serviceable: false,
        message: `${label}: ${getErrorMessage(error, `Unable to check ${label.toLowerCase()} serviceability.`)}`,
      };
    }
  };

  const focusRouteServiceabilityError = () => {
    window.setTimeout(() => {
      routeServiceabilityErrorRef.current?.focus();
      routeServiceabilityErrorRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 80);
  };

  const handleContinue = async () => {
    if (!serviceable || !routeReady || typeof window === "undefined") {
      return;
    }

    setRouteServiceabilityLoading(true);
    setRouteServiceabilityErrors([]);

    const results = await Promise.all([
      checkLocationServiceability(pickupLocation, "Pickup", pincode),
      checkLocationServiceability(dropLocation, "Drop"),
    ]);
    const failedChecks = results.filter((result) => !result.serviceable);

    setRouteServiceabilityLoading(false);

    if (failedChecks.length > 0) {
      setRouteServiceabilityErrors(failedChecks.map((result) => result.message));
      focusRouteServiceabilityError();
      return;
    }

    window.sessionStorage.setItem(
      "landingRouteDraft",
      JSON.stringify({
        pincode,
        pickupLocation,
        dropLocation,
      })
    );

    navigate(ROUTES.bookVehicle);
  };

  const handleSwapRoute = () => {
    if (!pickupLocation || !dropLocation) {
      return;
    }

    setPickupLocation(dropLocation);
    setDropLocation(pickupLocation);
    setRouteServiceabilityErrors([]);
  };

  const handleResetRoute = () => {
    setPickupLocation(null);
    setDropLocation(null);
    setRouteServiceabilityErrors([]);
  };

  const handleFaqToggle = (index) => {
    setOpenFaqIndex((currentIndex) => (currentIndex === index ? null : index));
  };

  const handleCustomerLogin = () => {
    [
      "landingBookingFlow",
      "landingRouteDraft",
      "landingSelectedVehicle",
    ].forEach((key) => window.sessionStorage.removeItem(key));
    navigate(ROUTES.login);
  };

  return (
    <div className="landing-page">
      <AppTopbar
        brandHref={ROUTES.home}
        title={companyInfo.name}
        navId="landing-navbar-nav"
        pillText="Route Setup"
        actions={(
          <>
            <Button variant="outline-light" onClick={handleCustomerLogin}>
              For Customer
            </Button>
            <Button variant="light" onClick={() => navigate(ROUTES.driverLogin)}>
              For Driver
            </Button>
          </>
        )}
      />

      <section className="quick-strip-section">
        <Container>
          <div className="quick-strip">
            <div className="quick-strip-copy">
              <strong>{companyInfo.name}</strong>
              <span>{companyInfo.tagline}</span>
            </div>
            <div className="quick-strip-pills">
              {quickHighlights.map((item) => (
                <span key={item}>{item}</span>
              ))}
            </div>
          </div>
        </Container>
      </section>

      <section className="estimator-section">
        <Container>
          <div className="section-intro text-center">
            <span className="section-kicker">Start Booking</span>
          </div>

          <Row className="justify-content-center">
            <Col lg={8}>
              <div className="estimator-form">
                <div className="route-section-shell landing-serviceability-shell">
                  <div className="route-section-heading">
                    <span className="route-section-title">Serviceability</span>
                    <small>Enter pickup pincode to check whether RedGaady is available before adding route details.</small>
                  </div>

                  <div className="landing-serviceability-form">
                    <Form.Control
                      value={pincode}
                      onChange={(event) => {
                        setPincode(event.target.value.replace(/\D/g, "").slice(0, 6));
                        setServiceability(null);
                        setServiceabilityError("");
                        setRouteServiceabilityErrors([]);
                        setPickupLocation(null);
                        setDropLocation(null);
                      }}
                      placeholder="Enter pickup pincode"
                      inputMode="numeric"
                      maxLength={6}
                    />
                    <Button onClick={handleCheckServiceability} disabled={serviceabilityLoading || pincode.length !== 6}>
                      {serviceabilityLoading ? <Spinner size="sm" className="me-2" /> : <FaLocationArrow />}
                      Check
                    </Button>
                  </div>

                  {serviceabilityError && <Alert variant="danger" className="mt-3 mb-0">{serviceabilityError}</Alert>}
                  {serviceability?.serviceable === false && (
                    <Alert variant="warning" className="mt-3 mb-0">
                      RedGaady is not serving pickup pincode {pincode} right now. Please enter a different pincode.
                    </Alert>
                  )}
                  {serviceable && (
                    <Alert variant="success" className="mt-3 mb-0">
                      Good news! RedGaady is available for pickup in pincode {pincode}. Add pickup and drop to continue.
                    </Alert>
                  )}
                </div>

                {serviceable && (
                  <div className="route-section-shell">
                    <div className="route-section-heading">
                      <span className="route-section-title">Route Details</span>
                      <small>Add pickup and drop to continue.</small>
                    </div>

                    <div className="location-form-stack">
                      <div className="form-group">
                        <label className="form-label">
                          Pick-up Location
                        </label>
                        <LocationSelector
                          label="Where are you picking up from?"
                          location={pickupLocation}
                          onSelect={(location) => {
                            setPickupLocation(location);
                            setRouteServiceabilityErrors([]);
                          }}
                        />
                        {pickupLocation && <div className="selected-location">Pickup selected: {pickupLocation.name}</div>}
                      </div>

                      <div className="form-group">
                        <label className="form-label">
                          Drop-off Location
                        </label>
                        <LocationSelector
                          label="Where are you dropping off?"
                          location={dropLocation}
                          onSelect={(location) => {
                            setDropLocation(location);
                            setRouteServiceabilityErrors([]);
                          }}
                        />
                        {dropLocation && <div className="selected-location">Drop selected: {dropLocation.name}</div>}
                      </div>
                    </div>

                    <div className="route-utility-shell">
                      <div className="route-progress-card">
                        <span className="route-progress-pill is-complete">Serviceable</span>
                        <span className={`route-progress-pill ${pickupLocation ? "is-complete" : ""}`}>Pickup</span>
                        <span className={`route-progress-pill ${dropLocation ? "is-complete" : ""}`}>Drop</span>
                        <span className="route-progress-pill">Next: Vehicle</span>
                      </div>

                      <div className="route-toolbar-actions">
                        <Button
                          variant="outline-secondary"
                          className="route-action-btn"
                          onClick={handleSwapRoute}
                          disabled={!routeReady}
                        >
                          Swap Stops
                        </Button>
                        <Button
                          variant="outline-secondary"
                          className="route-action-btn"
                          onClick={handleResetRoute}
                          disabled={!pickupLocation && !dropLocation}
                        >
                          Reset
                        </Button>
                      </div>
                    </div>

                    {routeServiceabilityErrors.length > 0 && (
                      <Alert
                        variant="danger"
                        ref={routeServiceabilityErrorRef}
                        tabIndex="-1"
                        className="mt-3"
                      >
                        <strong>Location serviceability check failed.</strong>
                        <ul className="mb-0 mt-2">
                          {routeServiceabilityErrors.map((routeServiceabilityError) => (
                            <li key={routeServiceabilityError}>{routeServiceabilityError}</li>
                          ))}
                        </ul>
                      </Alert>
                    )}

                    <div className="step-action-row">
                      <span className="step-action-spacer" />
                      <Button
                        className="btn-book-now step-proceed-btn"
                        size="lg"
                        onClick={handleContinue}
                        disabled={!routeReady || routeServiceabilityLoading}
                      >
                        {routeServiceabilityLoading ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            Checking...
                          </>
                        ) : (
                          "Continue"
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="faq-section">
        <Container>
          <div className="section-intro text-center">
            <span className="section-kicker">FAQ</span>
            <h2 className="section-title">Answers before you continue</h2>
            <p className="section-copy">
              A quick guide to the new page-by-page booking flow before login.
            </p>
          </div>

          <div className="faq-shell">
            {faqItems.map((item, index) => {
              const isOpen = openFaqIndex === index;

              return (
                <div key={item.question} className={`faq-item ${isOpen ? "is-open" : ""}`}>
                  <button
                    type="button"
                    className="faq-trigger"
                    onClick={() => handleFaqToggle(index)}
                    aria-expanded={isOpen}
                    aria-controls={`landing-faq-panel-${index}`}
                  >
                    <span className="faq-question">{item.question}</span>
                    <span className="faq-icon" aria-hidden="true">
                      {isOpen ? "-" : "+"}
                    </span>
                  </button>
                  {isOpen ? (
                    <div id={`landing-faq-panel-${index}`} className="faq-panel">
                      <p>{item.answer}</p>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </Container>
      </section>
    </div>
  );
};

export default Landing;
